import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Lock, Check, Copy, ArrowLeft, FlaskConical, ChevronRight, ClipboardCheck } from 'lucide-react';
import { DEMO_CAMPAIGN, DEMO_STEPS } from '../data/demoCampaignSteps';
import { getTrackingSettings } from '../utils/api';

const STORAGE_KEY = 'demo_campaign_completed_steps';
const JOB_VALUE_KEY = 'demo_campaign_job_value';

const HeaderBlock = ({ label, children }) => (
  <div>
    <p className="text-base font-black tracking-widest text-orange mb-1.5">{label}</p>
    <div className="text-light-gray text-sm leading-relaxed">{children}</div>
  </div>
);

const CopyBlock = ({ label, content }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="bg-black border border-divider rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-divider">
        <span className="text-xs font-bold text-lime uppercase">{label}</span>
        <button
          data-testid="demo-copy-btn"
          onClick={() => {
            navigator.clipboard.writeText(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="flex items-center gap-1.5 text-xs text-gray hover:text-white"
        >
          {copied ? <><Check size={12} className="text-lime" /> Copied</> : <><Copy size={12} /> Copy</>}
        </button>
      </div>
      <pre className="p-4 text-sm text-white whitespace-pre-wrap font-sans">{content}</pre>
    </div>
  );
};

export default function DemoCampaign() {
  const [completed, setCompleted] = useState(() => {
    const v = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    return isNaN(v) ? 0 : v;
  });
  const [openStep, setOpenStep] = useState(null);
  const [jobValue, setJobValue] = useState(() => {
    const v = parseFloat(localStorage.getItem(JOB_VALUE_KEY));
    return isNaN(v) ? 0 : v;
  });

  useEffect(() => {
    getTrackingSettings()
      .then((res) => {
        const v = parseFloat(res.data.avg_job_value);
        if (v > 0) {
          setJobValue(v);
          localStorage.setItem(JOB_VALUE_KEY, String(v));
        }
      })
      .catch(() => {});
  }, []);

  const activeStep = Math.min(completed + 1, DEMO_STEPS.length);

  const markDone = (stepId) => {
    const next = Math.max(completed, stepId);
    setCompleted(next);
    localStorage.setItem(STORAGE_KEY, String(next));
    setOpenStep(stepId < DEMO_STEPS.length ? stepId + 1 : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetProgress = () => {
    setCompleted(0);
    localStorage.setItem(STORAGE_KEY, '0');
    setOpenStep(null);
  };

  return (
    <Layout variant="light">
      <div data-testid="demo-campaign-page" className="max-w-3xl">
        <Link to="/vault" className="text-orange text-sm hover:underline flex items-center gap-1 mb-6">
          <ArrowLeft size={14} /> Back to Campaigns
        </Link>

        <div className="flex items-center gap-2 mb-2">
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-orange/15 text-orange border border-orange/40 flex items-center gap-1.5">
            <FlaskConical size={12} /> UX PROTOTYPE
          </span>
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/5 text-gray-light border border-divider">
            {DEMO_CAMPAIGN.category}
          </span>
        </div>
        <h1 className="text-4xl font-black text-white mb-2">{DEMO_CAMPAIGN.name}</h1>
        <p className="text-light-gray mb-8">{DEMO_CAMPAIGN.tagline}</p>

        <div className="mb-8 p-4 rounded-xl border border-orange/40 bg-card-bg flex items-center gap-3 flex-wrap" data-testid="demo-prereq-link">
          <ClipboardCheck size={18} className="text-orange shrink-0" />
          <p className="text-sm text-light-gray flex-1">
            First campaign? Make sure you have the 5 basics ready (Facebook page, ad payment method, your job value, and more).
          </p>
          <Link to="/start-here" className="text-orange font-bold text-sm hover:underline whitespace-nowrap">
            Open the Before You Start checklist →
          </Link>
        </div>

        <div className="space-y-3" data-testid="demo-steps-list">
          {DEMO_STEPS.map((step) => {
            const isDone = step.id <= completed;
            const isActive = step.id === activeStep && !isDone;
            const isLocked = step.id > activeStep;
            const isOpen = openStep === step.id && !isLocked;

            return (
              <div
                key={step.id}
                data-testid={`demo-step-${step.id}`}
                className={`rounded-xl border transition-colors ${
                  isLocked
                    ? 'border-divider bg-card-bg/40 opacity-50'
                    : isActive
                    ? 'border-lime bg-card-bg'
                    : 'border-divider bg-card-bg'
                }`}
              >
                <button
                  data-testid={`demo-step-toggle-${step.id}`}
                  disabled={isLocked}
                  onClick={() => setOpenStep(isOpen ? null : step.id)}
                  className="w-full flex items-center gap-4 p-5 text-left disabled:cursor-not-allowed"
                >
                  <span
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm shrink-0 ${
                      isDone
                        ? 'bg-lime text-black'
                        : isActive
                        ? 'bg-lime/15 text-lime border-2 border-lime'
                        : 'bg-white/5 text-gray border border-divider'
                    }`}
                  >
                    {isDone ? <Check size={18} /> : isLocked ? <Lock size={15} /> : step.id}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className={`block font-bold ${isLocked ? 'text-gray' : 'text-white'}`}>
                      Step {step.id}: {step.title}
                    </span>
                    <span className="block text-sm text-gray truncate">
                      {isLocked ? `Unlocks after Step ${step.id - 1}` : step.shortDesc}
                    </span>
                  </span>
                  {!isLocked && (
                    <ChevronRight
                      size={18}
                      className={`text-gray shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                    />
                  )}
                </button>

                {isOpen && (
                  <div className="px-5 pb-6 border-t border-divider pt-6 space-y-6" data-testid={`demo-step-content-${step.id}`}>
                    <HeaderBlock label="WHAT THIS IS">{step.whatThisIs}</HeaderBlock>
                    <HeaderBlock label="WHY YOU'RE USING IT">{step.whyUsing}</HeaderBlock>
                    <HeaderBlock label="CHANNEL">{step.channel}</HeaderBlock>
                    <HeaderBlock label="WHEN TO USE IT">{step.whenToUse}</HeaderBlock>
                    <HeaderBlock label="WHAT TO DO">
                      <ol className="list-decimal list-inside space-y-1.5">
                        {step.whatToDo.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                    </HeaderBlock>
                    <HeaderBlock label="METRICS TO WATCH">
                      <ul className="space-y-1.5">
                        {step.metrics.map((m, i) => <li key={i}>📊 {m}</li>)}
                      </ul>
                    </HeaderBlock>
                    <HeaderBlock label="WHAT SUCCESS LOOKS LIKE (ROI)">
                      <p className="text-lime font-medium" data-testid={`demo-roi-${step.id}`}>
                        {step.roiCalc ? step.roiCalc(jobValue > 0 ? jobValue : 450) : step.roi}
                      </p>
                      {step.roiCalc && jobValue <= 0 && (
                        <p className="text-xs text-orange mt-1.5">
                          These numbers use a $450 example job. Enter what YOU charge on the campaign's Performance card and this updates to your real math.
                        </p>
                      )}
                      {step.roiCalc && jobValue > 0 && (
                        <p className="text-xs text-gray mt-1.5">
                          Based on your ${jobValue.toLocaleString()} job price — change it anytime on the campaign's Performance card.
                        </p>
                      )}
                    </HeaderBlock>
                    <HeaderBlock label="▶ NEXT STEP">{step.nextStep}</HeaderBlock>

                    <div className="space-y-4">
                      {step.assets.map((a, i) => <CopyBlock key={i} label={a.label} content={a.content} />)}
                    </div>

                    {!isDone && (
                      <button
                        data-testid={`demo-step-done-${step.id}`}
                        onClick={() => markDone(step.id)}
                        className="w-full py-3 rounded-lg bg-lime text-black font-bold hover:bg-lime/90"
                      >
                        {step.id < DEMO_STEPS.length
                          ? `✓ I did this — unlock Step ${step.id + 1}`
                          : '✓ Done — campaign complete!'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex items-center justify-between">
          {completed >= DEMO_STEPS.length && (
            <p className="text-lime font-bold" data-testid="demo-complete-msg">🎉 All steps complete — Bob's phone is ringing.</p>
          )}
          <button data-testid="demo-reset-btn" onClick={resetProgress} className="text-xs text-gray hover:text-white ml-auto">
            Reset progress
          </button>
        </div>
      </div>
    </Layout>
  );
}
